/**
 * Pages disposant d'une version anglaise pré-rendue sous /en/…
 *
 * Source unique, sans dépendance (importée aussi par middleware.ts à la
 * racine, qui tourne sur Vercel) : routes client, pré-rendu, sitemap,
 * hreflang et redirection 301 des anciennes URL ?lang=en en dérivent.
 * Le français reste la version par défaut à la racine.
 */
export const EN_PREFIX = "/en";

/** Chemin FR → chemin EN. */
export const EN_ALTERNATES: Readonly<Record<string, string>> = {
  "/": "/en",
  "/ai": "/en/ai",
  "/pricing": "/en/pricing",
  "/vs/doodle": "/en/vs/doodle",
  "/vs/framadate": "/en/vs/framadate",
};

const normalize = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

/**
 * Guides : /guides et /guides/<slug> ont leur version /en/guides[/<slug>].
 * Motif plutôt que liste : un guide ajouté dans guides.fr.json et guides.en.json
 * est couvert sans modification ici (la parité des slugs est testée).
 * Copie dans middleware.ts (cf. note de MIDDLEWARE_EN_ALTERNATES).
 */
const FR_GUIDES_RE = /^\/guides(?:\/[^/]+)?$/;
const EN_GUIDES_RE = /^\/en\/guides(?:\/[^/]+)?$/;

/** Version /en/ d'un chemin FR, ou null s'il n'en a pas. */
export function enPathFor(frPath: string): string | null {
  const p = normalize(frPath);
  if (FR_GUIDES_RE.test(p)) return `${EN_PREFIX}${p}`;
  return EN_ALTERNATES[p] ?? null;
}

/** Version FR d'un chemin /en/…, ou null si ce n'est pas une page EN connue. */
export function frPathFor(enPath: string): string | null {
  const p = normalize(enPath);
  if (EN_GUIDES_RE.test(p)) return p.slice(EN_PREFIX.length);
  for (const [fr, en] of Object.entries(EN_ALTERNATES)) {
    if (en === p) return fr;
  }
  return null;
}

/**
 * Chemin à utiliser pour un lien interne : sur une page /en/…, la version
 * anglaise de la cible si elle existe, sinon la cible telle quelle (page sans
 * version /en/, par exemple /about ou /create).
 */
export function localizedPath(currentPathname: string, target: string): string {
  if (!isEnPath(currentPathname)) return target;
  return enPathFor(target) ?? target;
}

/** Vrai pour /en et /en/… (quelle que soit la page). */
export function isEnPath(pathname: string): boolean {
  return pathname === EN_PREFIX || pathname.startsWith(`${EN_PREFIX}/`);
}

export function enPrerenderedPaths(): string[] {
  return Object.values(EN_ALTERNATES);
}

/**
 * hreflang des pages FR ↔ /en/ (mêmes valeurs que SEOHead), par chemin,
 * pour le sitemap. x-default sur la version anglaise.
 */
export function hreflangAlternates(
  extra: Readonly<Record<string, string>> = {},
): Record<string, [string, string][]> {
  const out: Record<string, [string, string][]> = {};
  for (const [fr, en] of Object.entries({ ...EN_ALTERNATES, ...extra })) {
    const links: [string, string][] = [
      ["fr", fr],
      ["en", en],
      ["es", `${fr}?lang=es`],
      ["x-default", en],
    ];
    out[fr] = links;
    out[en] = links;
  }
  return out;
}
